import type { LensCardData } from '~/types/leLens';

// Cards whose records arrive as positional arrays rather than named objects.
const positionalFields: Record<string, Array<string>> = {
  forecasts: [
    'date_axis',
    'offer_country_name',
    'offer_region',
    'vendor_name',
    'tier',
    'actual_margin',
    'expected_margin',
    'blended_margin',
    'source_records',
  ],
  schedule: ['offer_country_name', 'tier', 'vendor_name', 'cells'],
};

/** Reject incomplete transport batches before rendering or exporting any figures. */
export function supplyEvidenceRows<T>(cards: LensCardData[], id: string): T[] {
  const card = cards.find((c) => c.cardId === id);
  if (!card || !['ready', 'empty'].includes(card.status))
    throw Error(card?.message || `${id} evidence is still loading.`);
  if (card.truncated) throw Error(`${id} evidence exceeds its row limit. Narrow the source window before exporting.`);
  // Batches carry their source order; keep it regardless of how the card rows arrive.
  const batches = [...(card.rows || [])].sort((a, b) => Number(a.batch) - Number(b.batch));
  const fields = positionalFields[id];
  const seen = new Set<number>();
  let total: number | undefined;
  const records = batches.flatMap((row) => {
    const batch = Number(row.batch),
      count = Number(row.record_count),
      sourceCount = Number(row.source_count);
    if (
      !Number.isSafeInteger(batch) ||
      batch < 0 ||
      seen.has(batch) ||
      !Number.isSafeInteger(count) ||
      count < 1 ||
      !Number.isSafeInteger(sourceCount) ||
      sourceCount < count ||
      (total !== undefined && total !== sourceCount)
    )
      throw Error(`Invalid ${id} evidence batch.`);
    seen.add(batch);
    total = sourceCount;
    const parsed: unknown = JSON.parse(String(row.payload));
    if (!Array.isArray(parsed) || parsed.length !== count) throw Error(`Invalid ${id} evidence batch.`);
    return parsed.map((record) => {
      if (!fields || !Array.isArray(record)) return record as T;
      if (record.length !== fields.length) throw Error(`Invalid ${id} evidence record.`);
      return Object.fromEntries(fields.map((field, i) => [field, record[i]])) as T;
    });
  });
  if (records.length !== (total ?? 0) || [...seen].some((batch) => batch >= seen.size))
    throw Error(`Incomplete ${id} evidence. Figures and export are withheld.`);
  return records;
}

/** A value every batch of a card repeats, such as the schedule's shading bounds. Undefined when the card has no rows. */
export function supplyBatchValue(cards: LensCardData[], id: string, column: string): unknown {
  return cards.find((c) => c.cardId === id)?.rows?.[0]?.[column];
}
